import { useTranslation } from 'react-i18next'
import { generatePath, useNavigate } from 'react-router-dom'

import { InceptionTypeInputEnum } from '@/graphql/purchase-plus/generated/purchasePlus_graphql'
import useSessionUser from '@/modules/access/hooks/useSessionUser'
import { useCreateProduct } from '@/modules/admin-center/hooks/useCreateProduct'
import usePurchaserCreatePricedCataloguedProduct from '@/modules/catalogs/hooks/useCreatePricedCatalogedProduct'
import { useCreateOrUpdatePartyProduct } from '@/modules/products/hooks/useCreateOrUpdatePartyProduct'
import { CreateProductFormInputs } from '@/modules/shared/components/create-product-modal/CreateProductFormSchema'
import { UseCreateAndAddProductProps } from '@/modules/shared/components/create-product-modal/hooks/useCreateAndAddProductRequisition'
import { displayToast } from '@/modules/shared/components/toast/displayToast'
import { useAlert } from '@/modules/shared/hooks/useAlert'
import useCreatePricedCatalogedProduct from '@/modules/supplier/hooks/useCreatePricedCataloguedProduct'

export function useCreateAndAddProductCatalog({ quickAdd, refetch, onCloseModal, id }: UseCreateAndAddProductProps) {
  const { currentOrganisation } = useSessionUser()
  const { t } = useTranslation()
  const [createProduct, { loading: createProductLoading }] = useCreateProduct()

  const [purchaserCreatePricedCataloguedProduct, { loading: purchaserCreatePricedCatalogueProductLoading }] =
    usePurchaserCreatePricedCataloguedProduct()

  const [supplierCreatePricedCataloguedProduct, { loading: supplierCreatePricedCatalogueProductLoading }] =
    useCreatePricedCatalogedProduct()

  const navigate = useNavigate()
  const { alertDialog } = useAlert()

  const [createOrUpdatePartlyProduct] = useCreateOrUpdatePartyProduct()

  const onCreateAndAddProductCatalog = (input: CreateProductFormInputs, isAPurchaser?: boolean) => {
    const {
      categoryId,
      brand,
      itemDescription,
      itemMeasure,
      itemSize,
      itemPackName,
      itemSellPackName,
      itemSellQuantity,
      unitPrice,
      taxPercentage,
      code,
      externalId,
    } = input
    createProduct({
      variables: {
        input: {
          categoryId,
          brand,
          itemDescription,
          locale: 'en',
          itemMeasure,
          itemSize,
          itemPackName: itemPackName === 'null' ? null : itemPackName,
          itemSellPackName,
          itemSellQuantity,
          externalId,
          inceptionType: InceptionTypeInputEnum.Manual,
          ownerCode: isAPurchaser ? code : undefined,
        },
      },
      onCompleted: async (data) => {
        const createdProductId = Number(data.createProduct.product?.id)
        if (code) {
          await createOrUpdatePartlyProduct({
            variables: {
              input: {
                productId: createdProductId,
                organisationId: Number(currentOrganisation?.id),
                code: code,
              },
            },
          })
        }
        const createCatalogProductPayload = {
          catalogueId: Number(id),
          productId: createdProductId,
          sellUnitPrice: unitPrice,
          sellUnitTaxPercentage: taxPercentage,
        }

        const onCompleted = () => {
          if (quickAdd) {
            onCloseModal && onCloseModal()
            displayToast({ title: t('general.productAdded', 'Product Added') })
            refetch && refetch()
          } else {
            navigate(generatePath('/catalogs/:catalogId/products', { catalogId: String(id) }))
          }
        }

        if (currentOrganisation?.isAPurchaser) {
          purchaserCreatePricedCataloguedProduct({
            variables: {
              input: createCatalogProductPayload,
            },
            onCompleted: () => {
              onCompleted()
            },
          })
        } else {
          supplierCreatePricedCataloguedProduct({
            variables: {
              input: createCatalogProductPayload,
            },
            onCompleted: () => {
              onCompleted()
            },
          })
        }
      },
      onError() {
        alertDialog({ type: 'error' })
      },
    })
  }

  return {
    onCreateAndAddProductCatalog,
    loading:
      createProductLoading ||
      purchaserCreatePricedCatalogueProductLoading ||
      supplierCreatePricedCatalogueProductLoading,
  }
}
